// Separação entre LEGENDA e HASHTAGS no front — espelho de
// backend/src/services/captionHashtags.ts.
//
// O publicador junta caption + hashtagsFlat. Quando a caption já termina com o
// bloco de hashtags (assets gerados antes da normalização do backend, ou caption
// editada à mão), o post saía com as tags duas vezes. Aqui a legenda é separada
// do bloco final de tags e as duas listas são unidas sem repetição — o que a tela
// mostra passa a ser exatamente o que vai publicado.
//
// Hashtag no meio de uma frase é escolha de redação e não é tocada.

const HASHTAG_RE = /^#[\p{L}\p{N}_]+$/u;

const tagKey = (tag: string) => tag.trim().toLowerCase();

export function parseHashtagList(value?: string | string[] | null): string[] {
  const list = Array.isArray(value) ? value : String(value || '').split(/\s+/);
  return list.map(t => String(t || '').trim()).filter(t => HASHTAG_RE.test(t));
}

export function mergeHashtags(...lists: Array<string | string[] | null | undefined>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const list of lists) {
    for (const tag of parseHashtagList(list)) {
      const key = tagKey(tag);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(tag);
    }
  }
  return out;
}

// Tira o bloco de hashtags do FIM do texto. Nunca devolve texto vazio: caption
// composta só de hashtags fica intacta.
export function splitTrailingHashtags(text?: string | null): { text: string; tags: string[] } {
  const value = typeof text === 'string' ? text : '';
  if (!value.includes('#')) return { text: value, tags: [] };

  const lines = value.split('\n');
  const tags: string[] = [];
  let end = lines.length;

  // (1) linhas finais compostas somente de hashtags.
  while (end > 0) {
    const line = lines[end - 1].trim();
    if (!line) { end--; continue; }
    const tokens = line.split(/\s+/);
    if (tokens.every(t => HASHTAG_RE.test(t))) {
      tags.unshift(...tokens);
      end--;
      continue;
    }
    break;
  }
  const kept = lines.slice(0, end);

  // (2) rabo de 2+ hashtags na última linha com texto.
  let lastTextIdx = -1;
  for (let i = kept.length - 1; i >= 0; i--) {
    if (kept[i].trim()) { lastTextIdx = i; break; }
  }
  if (lastTextIdx >= 0) {
    const tokens = kept[lastTextIdx].trim().split(/\s+/);
    let cut = tokens.length;
    while (cut > 0 && HASHTAG_RE.test(tokens[cut - 1])) cut--;
    const trailing = tokens.slice(cut);
    if (trailing.length >= 2 && cut > 0) {
      tags.unshift(...trailing);
      kept[lastTextIdx] = tokens.slice(0, cut).join(' ');
    }
  }

  const cleaned = kept.join('\n').replace(/\n{3,}/g, '\n\n').trim();
  if (!cleaned) return { text: value, tags: [] };
  return { text: cleaned, tags: mergeHashtags(tags) };
}

// Legenda e hashtags prontas para exibir e publicar, sem repetição.
export function composeCaption(caption?: string | null, hashtagsFlat?: string | null): {
  body: string;
  tagLine: string;
  full: string;
} {
  const { text, tags } = splitTrailingHashtags(caption);
  const body = text.trim();
  // Tag que continua DENTRO da legenda (usada como palavra da frase, ou caption
  // composta só de hashtags, que nunca é esvaziada) não entra na linha de tags —
  // repetir no mesmo post não amplia alcance, só suja a legenda.
  const inBody = new Set(parseHashtagList(body.split(/\s+/)).map(tagKey));
  const tagLine = mergeHashtags(tags, hashtagsFlat)
    .filter(tag => !inBody.has(tagKey(tag)))
    .join(' ');
  const full = tagLine ? (body ? `${body}\n\n${tagLine}` : tagLine) : body;
  return { body, tagLine, full };
}
